/* import __COLOCATED_TEMPLATE__ from './ftw-dec.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type CircuitService from '../services/circuit';

// This function displays very big numbers and very small numbers with as many trailing or leading zeros as necessary.
// It avoids ever devolving into exponential notation, something we can't guarantee with the language-supplied methods.
const toFixedAlways = function (x: number) {
  // Default behavior does the right thing for values between 1 and 10^20, positive or negative, so start there
  let result = Math.round(x).toString();
  //Can't take log10(0) and default behavior does the right thing for zero as well
  if (x !== 0) {
    const posX = Math.abs(x);
    const e = Math.round(Math.log10(posX));
    if (e < 0) {
      //TODO: Do we need this clause? We only really care about big numbers here - we don't do fractions
      const scale = Math.pow(10, -e - 1);
      const zeroes = '0'.repeat(-e - 1);
      const digitsAfterDecimal = (posX * scale).toString().split('.')[1];
      result = '0.' + zeroes + digitsAfterDecimal;
    } else if (e > 20) {
      const scale = Math.pow(10, e - 20);
      const zeroes = '0'.repeat(e - 20 + 1);
      result = Math.round(posX / scale).toString() + zeroes;
    }
    if (posX != x) {
      result = '-' + result;
    }
  }
  return result;
};

interface FTWDecimalInputSignature {
  Args: {
    label: string;
    isExceededFtw?: boolean;
    ftwChanged: (value: number) => void;
  };
}

export default class FTWDecimalInput extends Component<FTWDecimalInputSignature> {
  @service declare circuit: CircuitService;

  get ftwDisplay() {
    let result = '';
    // If we have an error that involves an error message, blank the value out
    // Otherwise, show the value.
    if (!this.circuit.hasCriticalError && !this.args.isExceededFtw) {
      result = isNaN(this.circuit.ftw) ? '' : toFixedAlways(this.circuit.ftw);
    }
    return result;
  }
  @action localFtwChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const isValidDecimal = /^[0-9]+$/.test(target.value);
    if (this.ftwDisplay !== target.value) {
      // If we can parse the value, parse it and hand it off to the panel to figure out if the number makes sense
      if (isValidDecimal) {
        this.args.ftwChanged(parseInt(target.value));
      } else {
        // Fall back to the most recent successfully-parsed value we can use
        // even if it turned out subsequently to be invalid for other reasons
        if (this.circuit.uncommittedFtwValue !== null) {
          this.args.ftwChanged(this.circuit.uncommittedFtwValue);
        } else {
          this.args.ftwChanged(this.circuit.ftw);
        }
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    FtwDec: typeof FTWDecimalInput;
  }
}
